/**
 * 仓库映射弹窗（添加 / 编辑共用）— 类型、步骤文案、接口字段映射与文案工具。
 *
 * 与物流渠道映射弹窗结构类似，但：
 * - 顶部确认的是「VC 仓库地址」而非物流渠道
 * - 表格列为 1P 仓 / 三方仓地址 / 状态（添加）或 添加人时间（编辑）
 * - 行操作：添加态「确认映射」，编辑态「解除映射」
 */
import type { UiTagType } from "@shared/components/UiTag/index.vue";
import type { PaginatedQuery } from "@shared/types/list";
import { DEFAULT_PAGE_SIZE } from "@shared/types/list";
import { t } from "@shared/utils/i18n";
import type { VcWarehouseMappingGroupRow, VcWarehouseMappingKind } from "./data";

/** 弹窗模式：添加映射 | 编辑映射 */
export type WarehouseMappingDialogMode = "add" | "edit";

/** 添加态顶部三步（仅展示进度，不切换页面） */
export const WAREHOUSE_MAPPING_DIALOG_STEPS = [
    { key: 1, label: t("确认VC仓库地址") },
    { key: 2, label: t("搜索企业三方仓") },
    { key: 3, label: t("添加映射关系") },
] as const;

/** 弹窗列表查询参数 */
export interface WarehouseMappingDialogQueryParam extends PaginatedQuery {
    /** VC 平台仓库 ID（platformWarehouseId） */
    platformWarehouseId: string;
    corpId: string | "";
    keyword: string;
    /**
     * 配对状态：添加弹窗传 1，编辑弹窗传 2（请求体以数组提交，如 [1] / [2]）
     * 统一走 external/candidateList
     */
    pairStatus: 1 | 2;
}

export const DEFAULT_WAREHOUSE_MAPPING_DIALOG_QUERY: Omit<WarehouseMappingDialogQueryParam, "platformWarehouseId" | "pairStatus"> = {
    corpId: "",
    keyword: "",
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
};

/**
 * 候选列表 items[]（POST /manage/warehousePlatformMapping/external/candidateList）。
 * 字段兼容添加 / 编辑两套命名。
 */
export interface WarehouseMappingDialogItemApi {
    /** 1P / 外部仓库 ID（warehouse_external 主键，雪花 string） */
    externalWarehouseId: string;
    corpId: string;
    corpName: string;
    /** 1P 仓库名称 */
    systemWarehouseName?: string;
    externalWarehouseName?: string;
    warehouseName?: string;
    warehouseCode?: string;
    externalWarehouseCode?: string;
    isQianyaWarehouse?: number;
    status?: number;
    statusLabel?: string;
    /** candidateList */
    mapped?: boolean;
    mappingId?: number;
    mappedToCurrentVc?: boolean;
    /** mapping/list */
    id?: number;
    createdBy?: string;
    createTime?: string;
    creatorName?: string;
    createdAt?: string;
    // 地址
    countryCode?: string;
    zipCode?: string;
    state?: string;
    stateCode?: string;
    city?: string;
    cityLabel?: string;
    district?: string;
    address1?: string;
    address2?: string;
}

/** 弹窗表格行 */
export interface WarehouseMappingDialogRow {
    id: string;
    externalWarehouseId: string;
    corpId: string;
    corpName: string;
    /** 1P 仓库名称 */
    systemWarehouseName: string;
    warehouseName: string;
    warehouseCode: string;
    isQianyaWarehouse: boolean;
    status: number;
    statusLabel: string;
    mappingId: number | null;
    /** 是否已映射到当前 VC 仓（编辑列表恒为 true） */
    isMapped: boolean;
    addressText: string;
    createdBy: string;
    createTime: string;
}

export function mapWarehouseMappingDialogItem(item: WarehouseMappingDialogItemApi, options?: { forceMapped?: boolean }): WarehouseMappingDialogRow {
    const status = item.status ?? 2;
    const statusLabel = item.statusLabel || getWarehouseMappingDialogStatusLabel(status);
    const mappingIdRaw = item.mappingId ?? item.id ?? 0;
    const mappingId = mappingIdRaw > 0 ? mappingIdRaw : null;
    const isMapped = options?.forceMapped ? true : Boolean(item.mappedToCurrentVc) || (Boolean(item.mapped) && mappingId != null);
    const warehouseName = item.warehouseName || item.externalWarehouseName || "";
    const warehouseCode = item.warehouseCode || item.externalWarehouseCode || "";
    const systemWarehouseName = item.systemWarehouseName || item.externalWarehouseName || warehouseName;

    const externalWarehouseId = item.externalWarehouseId;
    return {
        id: externalWarehouseId,
        externalWarehouseId,
        corpId: item.corpId,
        corpName: item.corpName,
        systemWarehouseName,
        warehouseName,
        warehouseCode,
        isQianyaWarehouse: Boolean(item.isQianyaWarehouse),
        status,
        statusLabel,
        mappingId,
        isMapped,
        addressText: formatWarehouseMappingAddress(item),
        createdBy: item.createdBy || item.creatorName || "",
        createTime: item.createTime || item.createdAt || "",
    };
}

export function getWarehouseMappingDialogStatusLabel(status: number): string {
    return status === 1 ? t("启用中") : t("已禁用");
}

/** 设计稿：启用中偏红强调，已禁用中性灰 */
export function getWarehouseMappingDialogStatusTagType(status: number): UiTagType {
    return status === 1 ? "error" : "neutral";
}

/** 三方仓名称/代码/是否千亚仓 */
export function formatThirdPartyWarehouseCell(row: WarehouseMappingDialogRow): string {
    const qianya = row.isQianyaWarehouse ? t("是") : t("否");
    return `${row.warehouseName || "--"}/${row.warehouseCode || "--"}/${qianya}`;
}

/** 国家/邮编/省州/城市/区县/详细地址1/详细地址2 */
export function formatWarehouseMappingAddress(
    item: Pick<WarehouseMappingDialogItemApi, "countryCode" | "zipCode" | "state" | "stateCode" | "city" | "cityLabel" | "district" | "address1" | "address2">,
): string {
    const parts = [item.countryCode, item.zipCode, item.state || item.stateCode, item.city || item.cityLabel, item.district, item.address1, item.address2].filter(Boolean);
    return parts.length ? parts.join(", ") : "--";
}

/** 弹窗顶部展示的 VC 仓摘要（由列表组头行转换） */
export interface WarehouseMappingDialogWarehouseInfo {
    /** PO/DF，添加映射时作为 add 接口的 channelType */
    warehouseKind: VcWarehouseMappingKind;
    platformWarehouseId: string;
    warehouseCode: string;
    warehouseName: string;
    country: string;
    province: string;
    zone: string;
    city: string;
    /** 完整地址展示文案 */
    fullAddress: string;
}

export function toWarehouseMappingDialogInfo(row: VcWarehouseMappingGroupRow): WarehouseMappingDialogWarehouseInfo {
    // 完整地址不含详细地址2
    const fullAddress = [row.groupAddress1, row.groupCity, row.groupProvince, row.groupCountry, row.groupZipCode].filter(Boolean).join(", ") || "--";
    return {
        warehouseKind: row.warehouseKind,
        platformWarehouseId: row.warehouseId,
        warehouseCode: row.warehouseCode,
        warehouseName: row.warehouseName,
        country: row.groupCountry || "--",
        province: row.groupProvince || "--",
        zone: row.groupZone || "--",
        city: row.groupCity || "--",
        fullAddress,
    };
}

/** VC 仓库映射 add 接口单条入参（POST /manage/warehousePlatformMapping/add，items 数组支持批量） */
export interface AddVcWarehouseMappingItem {
    platformCode: "amazon_vc";
    channelType: VcWarehouseMappingKind;
    platformWarehouseId: string;
    /** warehouse_external 主键，须 string，禁止 Number() */
    externalWarehouseId: string;
    remark?: string;
}

export function buildAddVcWarehouseMappingItem(warehouseInfo: WarehouseMappingDialogWarehouseInfo, externalWarehouseId: string): AddVcWarehouseMappingItem {
    return {
        platformCode: "amazon_vc",
        channelType: warehouseInfo.warehouseKind,
        platformWarehouseId: warehouseInfo.platformWarehouseId,
        externalWarehouseId,
    };
}

/** add / changeStatus 返回 data（与物流渠道映射同结构） */
export interface WarehouseMappingChangeStatusResultItem {
    succ: boolean;
    index: number;
    message: string;
    originalMessage: string;
}

export interface WarehouseMappingChangeStatusResult {
    total: number;
    failCount: number;
    succCount: number;
    resultList: WarehouseMappingChangeStatusResultItem[];
}

export function buildWarehouseMappingSuccessMessage(systemWarehouseName: string, warehouseCode: string): string {
    return `${systemWarehouseName || "--"}与【${warehouseCode || "--"}】映射成功`;
}

/** 解除映射确认文案：解除【1P仓】与【VC仓代码】的映射关系? */
export function buildWarehouseUnbindConfirmMessage(systemWarehouseName: string, warehouseCode: string): string {
    return t(`解除【${systemWarehouseName || "--"}】与【${warehouseCode || "--"}】的映射关系?`);
}

export function resolveWarehouseMappingChangeStatusView(result: WarehouseMappingChangeStatusResult, fallbackSuccessMessage: string): { success: boolean; message: string } {
    const failed = (result.resultList ?? []).find(item => !item.succ);
    if (result.failCount > 0 || failed) {
        return {
            success: false,
            message: failed?.message || failed?.originalMessage || t("映射失败"),
        };
    }
    const successItem = (result.resultList ?? []).find(item => item.succ);
    const apiMessage = successItem?.message || successItem?.originalMessage;
    return {
        success: true,
        message: apiMessage?.trim() ? apiMessage : fallbackSuccessMessage,
    };
}
